import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Loader2, Receipt } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { Button } from "../components/ui/button";
import { useCart } from "../contexts/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useI18n();
  const { refreshCart } = useCart();
  const [status, setStatus] = useState("verifying"); // verifying, paid, failed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    let count = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refreshCart();
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
      } catch {}
      count += 1;
      setAttempts(count);
      if (count >= 8) {
        setStatus("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshCart]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-6" data-testid="checkout-success-page">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-3xl bg-white border border-slate-200 p-10 text-center max-w-md w-full shadow-[0_24px_60px_rgba(15,15,20,0.08)]">
        {status === "verifying" && (
          <>
            <div className="w-16 h-16 rounded-full bg-purple-50 grid place-items-center mx-auto mb-4 animate-pulse">
              <Loader2 className="w-8 h-8 text-[#A855F7] animate-spin" />
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900">{t("checkout.verifying")}</h2>
            <p className="text-slate-500 mt-2">Attempt {attempts + 1} / 8</p>
          </>
        )}
        {status === "paid" && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="w-16 h-16 rounded-full bg-emerald-50 grid place-items-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-500" />
            </motion.div>
            <h2 className="font-display text-2xl font-bold text-slate-900">{t("checkout.success")}</h2>
            <p className="text-slate-500 mt-2">{t("checkout.success_sub")}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard/orders"><Button className="btn-neon rounded-full px-6" data-testid="view-orders-btn"><Receipt className="w-4 h-4 mr-2" /> {t("checkout.see_orders")}</Button></Link>
              <Link to="/catalog"><Button variant="outline" className="rounded-full px-6">{t("common.continue_shopping")}</Button></Link>
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 grid place-items-center mx-auto mb-4">
              <AlertCircle className="w-9 h-9 text-red-500" />
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900">{t("checkout.failed")}</h2>
            <p className="text-slate-500 mt-2">Please try again or check your dashboard.</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/cart"><Button className="btn-neon rounded-full px-6">{t("checkout.try_again")}</Button></Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
